// Плавающая мини-панель таблицы: появляется в поле листа справа от таблицы, когда курсор в ней (строки, графы, объединение,
// выравнивание графы, прочерк, головка, примечание, «ещё»). Живёт вне документа (плагин с React-корнем).
import { useRef, useState } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { Extension, type Editor } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import { Btn, MenuList, Popover } from '../../../ui/controls';
import { Icon } from '../../../ui/icons';
import { ctx } from '../../../core/ctx';
import type { MenuItemSpec } from '../../../core/registry';
import { currentAlign, tableCtx } from './model';

export const tableToolbarKey = new PluginKey('tfcTableToolbar');

function run(id: string, arg?: unknown) { ctx.run(id, arg); }

function Menu({ icon, label, title, items, testid }: { icon?: string; label?: string; title: string; items: MenuItemSpec[]; testid?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  return (
    <span ref={ref} className="tfc-tb-menu">
      <Btn icon={icon} label={label} title={title} onClick={() => setOpen(!open)} testid={testid}><Icon name="down" size={11} /></Btn>
      {open && <Popover anchor={ref.current} onClose={() => setOpen(false)}><MenuList items={items} onDone={() => setOpen(false)} /></Popover>}
    </span>
  );
}

function Toolbar({ editor }: { editor: Editor }) {
  const t = tableCtx(editor);
  if (!t) return null;
  const al = currentAlign(editor);
  const hasNote = t.sto.lastChild?.type.name === 'tableNote';
  const b = (id: string, icon: string, title: string, arg?: unknown, active?: boolean) => (
    <Btn icon={icon} title={title} onClick={() => run(id, arg)} active={active} testid={'tfc-tb-' + id.replace(/\./g, '-') + (arg !== undefined ? '-' + String(arg) : '')} />
  );
  return (
    <div className="tfc-toolbar" data-testid="tfc-table-toolbar" onMouseDown={(e) => e.preventDefault()}>
      {b('table.rowAbove', 'tfc-row-above', 'Вставить строку выше')}
      {b('table.rowBelow', 'tfc-row-below', 'Вставить строку ниже (Tab в последней ячейке - тоже)')}
      {b('table.colLeft', 'tfc-col-left', 'Вставить графу слева')}
      {b('table.colRight', 'tfc-col-right', 'Вставить графу справа')}
      <Menu icon="minus" title="Удалить" items={[{ command: 'table.delRow' }, { command: 'table.delCol' }, { separator: true }, { command: 'table.delete' }]} testid="tfc-tb-del" />
      <span className="tfc-tb-sep" />
      {b('table.merge', 'tfc-merge', 'Объединить ячейки (выделите их мышью или Shift+стрелками)')}
      {b('table.split', 'tfc-split', 'Разделить ячейку')}
      <span className="tfc-tb-sep" />
      {b('table.align', 'align-left', 'Графа: значения влево', 'left', al === 'left')}
      {b('table.align', 'align-center', 'Графа: значения по центру', 'center', al === 'center')}
      <Btn label="А" title="Выравнивание графы по СТО автоматически (боковик влево, значения по центру)" active={al === null}
        onClick={() => run('table.align', null)} testid="tfc-tb-align-auto" className="tfc-tb-auto" />
      {b('table.dash', 'tfc-dash', 'Прочерк «–» в пустой ячейке (4.9.21)')}
      <span className="tfc-tb-sep" />
      <Menu icon="tfc-header" label={String(t.headerRows)} title="Строк в головке таблицы"
        items={[1, 2, 3].filter((n) => n < t.map.height || n === 1).map((n) => ({ label: `Строк головки: ${n}`, checked: n === t.headerRows, command: 'table.headerRows', arg: n }))}
        testid="tfc-tb-head" />
      {b('table.note', 'note', hasNote ? 'Примечание к таблице уже есть (под таблицей)' : 'Примечание к таблице (4.10.7)')}
      <Menu icon="more" title="Ещё" testid="tfc-tb-more" items={[
        { command: 'table.props' },
        { separator: true },
        { command: 'table.sortAsc' }, { command: 'table.sortDesc' },
        { separator: true },
        { command: 'table.widthsEqual' }, { command: 'table.widthsAuto' },
        { command: 'table.fillDash' },
        { separator: true },
        { command: 'table.pasteData' },
        { command: 'table.delete' },
      ]} />
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ плагин-хост
class ToolbarHost {
  host: HTMLDivElement;
  root: Root;
  constructor(private view: EditorView, private editor: Editor) {
    this.host = document.createElement('div');
    this.host.className = 'tfc-float-host';
    document.body.appendChild(this.host);
    this.root = createRoot(this.host);
    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, true);
    window.addEventListener('resize', this.onScroll);
    this.update();
  }
  onScroll() { this.position(); }
  current(): HTMLElement | null {
    const t = tableCtx(this.editor);
    if (!t) return null;
    const focused = this.view.hasFocus() || this.host.contains(document.activeElement);
    if (!focused) return null;
    return this.view.nodeDOM(t.stoPos) as HTMLElement | null;
  }
  /** Панель - вертикально в правом поле листа, рядом с таблицей: не закрывает ни текст над таблицей, ни саму
   *  таблицу (щелчок по абзацу над таблицей всегда попадает в абзац). Нет места справа - слева от таблицы. */
  position() {
    const el = this.current();
    if (!el) { this.host.style.display = 'none'; return null; }
    const r = el.getBoundingClientRect();
    const h = this.host.offsetHeight || 420;
    const w = this.host.offsetWidth || 34;
    const top = Math.min(Math.max(8, r.top), Math.max(8, window.innerHeight - h - 8));
    let left = r.right + 8;
    if (left + w > window.innerWidth - 4) left = Math.max(4, r.left - w - 8);
    Object.assign(this.host.style, { display: 'block', top: `${top}px`, left: `${left}px` });
    // таблица ушла за край видимой области - прячем
    if (r.bottom < 40 || r.top > window.innerHeight) this.host.style.display = 'none';
    return el;
  }
  private raf = 0;
  /** Перерисовка - не синхронно (плагин обновляется и во время отрисовки React-дерева редактора). */
  update() {
    if (this.raf) return;
    this.raf = requestAnimationFrame(() => {
      this.raf = 0;
      if (this.dead) return;
      const el = this.position();
      this.root.render(el ? <Toolbar editor={this.editor} /> : null);
    });
  }
  private dead = false;
  destroy() {
    this.dead = true;
    if (this.raf) cancelAnimationFrame(this.raf);
    window.removeEventListener('scroll', this.onScroll, true);
    window.removeEventListener('resize', this.onScroll);
    const root = this.root; const host = this.host;
    setTimeout(() => { root.unmount(); host.remove(); }, 0);
  }
}

export const TableToolbarExtension = Extension.create({
  name: 'tfcTableToolbar',
  addProseMirrorPlugins() {
    const editor = this.editor;
    return [new Plugin({
      key: tableToolbarKey,
      view: (view) => {
        const h = new ToolbarHost(view, editor);
        const onBlur = () => setTimeout(() => h.update(), 0);
        // щелчок вне таблицы и вне панели - панель прячется сразу, до обработки щелчка редактором
        const onDown = (e: MouseEvent) => {
          const t = e.target as Node | null;
          if (!t || h.host.contains(t)) return;
          const cur = h.current();
          if (cur && !cur.contains(t)) h.host.style.display = 'none';
        };
        view.dom.addEventListener('blur', onBlur);
        view.dom.addEventListener('focus', onBlur);
        document.addEventListener('mousedown', onDown, true);
        return { update: () => h.update(), destroy: () => {
          view.dom.removeEventListener('blur', onBlur); view.dom.removeEventListener('focus', onBlur);
          document.removeEventListener('mousedown', onDown, true); h.destroy();
        } };
      },
    })];
  },
});
